<template>
  <MainLayout class="series-cards">
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-6 col-lg-7">
          <h5 class="action-header series-cards__header-title typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile"
              v-bind:title="`${$t('publishers.sub_header.series')} > ${seriesTitle}`">
            {{ $t('publishers.sub_header.series') }} > {{ seriesTitle }}
          </h5>

          <div class="typography--body-2 typography--color-secondary">
            <span class="mr-6">
              {{ $t('series_cards.meta.series_price', { price: seriesPrice }) }}
            </span>
            |
            <span class="ml-6 mr-6">
              {{ $t('series_cards.meta.card_price', { price: seriesCardPrice }) }}
            </span>
            |
            <span class="ml-6">
              {{ getShareLevelLabel(seriesShareLevel) }}
            </span>
          </div>
        </div>

        <div class="col-md-6 col-lg-5 hidden-sm-down">
          <div class="action-btn-row">
            <button class="btn btn-primary float-right ml-1 mr-1" v-on:click="seriesEditModalIsShown = true">
              {{ $t('publishers.modal.series_edit.title') }}
            </button>
            <button class="btn btn-primary float-right ml-1 mr-1" v-on:click="coverImageEditModalIsShown = true">
              {{ $t('publishers.headers.actions.upload_cover_image') }}
            </button>
            <button class="btn btn-outline-primary float-right mr-1" v-on:click="$root.$emit('show::modal', 'delete-series-modal')">
              {{ $t('publishers.headers.actions.remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-4 pt-1" slot="layout-body" v-if="loading">
      <div class="row">
        <div class="col-12 text-center">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled pb-5">
            {{ $t('common.loading') }}
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-4 pt-1" slot="layout-body" v-else>

      <ChapterView
        v-for="chapter in visibleChapters" v-bind:key="chapter.id"
        v-bind:chapter="chapter"
        v-bind:series-item="seriesItem"
        v-on:open-chapter-edit-modal="openChapterEditModal"
        v-on:open-remove-from-series-modal="openRemoveFromSeriesModal"
        v-on:open-remove-card-modal="openRemoveCardModal"
        class="row"
      />

      <InfiniteLoading :on-infinite="onInfinite" ref="infiniteLoading" class="w-100">
        <div slot="spinner">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled pb-5">
            {{ $t('common.loading') }}
          </div>
        </div>

        <div slot="no-more" class="col-12 text-center mb-5 pb-5">
        </div>
      </InfiniteLoading>


    </div>

    <div slot="modal-area">
      <ChapterEditModal
        v-if="chapterEditModalIsShown"
        v-bind:chapter="chapterForEdit"
        v-bind:series-item="seriesItem"
        v-on:cancel="resetChapterForEdit()"
        v-on:ok="resetChapterForEdit()"
      />

      <SeriesEditModal
        v-if="seriesEditModalIsShown"
        v-bind:series-item="seriesItem"
        v-on:cancel="seriesEditModalIsShown = false"
        v-on:ok="updateSeriesItem()"
      />

      <CoverImageEditModal
        v-if="coverImageEditModalIsShown"
        v-bind:series-item="seriesItem"
        v-on:cancel="coverImageEditModalIsShown = false"
        v-on:ok="updateSeriesItem()"
      />

      <BasicModal id="delete-series-modal" v-bind:title="$t('publishers.modal.remove_series.title')" v-on:ok="remove()">
        <div v-html="$t('publishers.modal.remove_series.message')"></div>
      </BasicModal>

      <BasicModal id="remove-from-series-modal" 
                  v-bind:title="$t('publishers.modal.remove_from_series.title')"
                  v-on:ok="removeFromSeries()"
                  v-on:cancel="resetCardIdForRemoveFromSeries()">
        <div v-html="$t('publishers.modal.remove_from_series.message')"></div>
      </BasicModal>

      <BasicModal id="remove-card-modal" 
                  v-bind:title="$t('publishers.modal.remove_publisher_card.title')"
                  v-on:ok="removeCard()"
                  v-on:cancel="resetCardIdForRemove()">
        <div v-html="$t('publishers.modal.remove_publisher_card.message')"></div>
      </BasicModal>
    </div>
  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty, sortBy } from 'lodash';
  import InfiniteLoading from 'vue-infinite-loading';
  import labels from '@/mixins/labels';
  import publisherOnly from '@/mixins/publisherOnly';
  import CoverImageEditModal from '../components/CoverImageEditModal';
  import ChapterEditModal from '../components/ChapterEditModal';
  import SeriesEditModal from '../components/SeriesEditModal';
  import ChapterView from '../components/ChapterView';

  export default {
    name: 'series-cards',

    mixins: [
      publisherOnly,
      labels,
    ],

    components: {
      CoverImageEditModal,
      ChapterEditModal,
      SeriesEditModal,
      InfiniteLoading,
      ChapterView,
    },

    data() {
      return {
        loading: false,
        seriesItem: null,
        visibleChapterIndex: -1,
        chapterEditModalIsShown: false,
        seriesEditModalIsShown: false,
        coverImageEditModalIsShown: false,
        chapterForEdit: null,
        cardIdForRemoveFromSeries: null,
        cardIdForRemove: null,
      };
    },

    computed: {
      ...mapGetters([
        'currentPublisherInfo',
        'chaptersBySeriesId',
        'cardsByChapterId',
        'subjectMap',
        'gradeMap',
        'series',
      ]),

      publisherId() {
        return this.currentPublisherInfo.id;
      },

      chapters() {
        const seriesId = this.$route.params.series_id;

        return sortBy(this.chaptersBySeriesId[seriesId], 'position');
      },

      subjectName() {
        return this.getSubjectLabel(this.seriesItem);
      },

      gradeName() {
        return this.getGradeLabel(this.seriesItem);
      },

      seriesTitle() {
        if (isNil(this.seriesItem)) {
          return '';
        }

        return `${this.gradeName} ${this.subjectName}: ${this.seriesItem.name}`;
      },

      seriesPrice() {
        if (isNil(this.seriesItem)) {
          return 0;
        }

        return this.seriesItem.price || 0;
      },

      seriesCardPrice() {
        if (isNil(this.seriesItem)) {
          return 0;
        }

        return this.seriesItem.card_price || 0;
      },

      seriesShareLevel() {
        if (isNil(this.seriesItem)) {
          return 0;
        }

        return this.seriesItem.share_level;
      },

      visibleChapters() {
        if (isNil(this.chapters)) {
          return [];
        }

        return this.chapters.slice(0, this.visibleChapterIndex + 1);
      },
    },

    methods: {
      ...mapActions([
        'removeCardFromSeries',
        'removePublisherCard',
        'fetchSeriesChapters',
        'fetchChapterCards',
        'fetchSeriesById',
        'removeSeries',
      ]),

      openChapterEditModal(chapter) {
        this.chapterForEdit = chapter;
        this.chapterEditModalIsShown = true;
      },

      openRemoveFromSeriesModal(cardId) {
        this.cardIdForRemoveFromSeries = cardId;
        this.$root.$emit('show::modal', 'remove-from-series-modal');
      },

      openRemoveCardModal(cardId) {
        this.cardIdForRemove = cardId;
        this.$root.$emit('show::modal', 'remove-card-modal');
      },

      resetChapterForEdit() {
        this.chapterEditModalIsShown = false;
        this.chapterForEdit = null;
      },

      resetCardIdForRemoveFromSeries() {
        this.cardIdForRemoveFromSeries = null;
      },

      resetCardIdForRemove() {
        this.cardIdForRemove = null;
      },

      updateSeriesItem() {
        this.seriesEditModalIsShown = false;
        this.coverImageEditModalIsShown = false;
        this.seriesItem = this.series[this.seriesItem.id];
      },

      remove() {
        this.removeSeries(this.seriesItem.id)
          .then(() => (
            this.$router.replace('/publishers/series')
          ));
      },

      removeFromSeries() {
        if (this.cardIdForRemoveFromSeries) {
          const params = {
            series_id: this.seriesItem.id,
            card_id: this.cardIdForRemoveFromSeries,
          };
  
          this.removeCardFromSeries(params);
        }
      },

      removeCard() {
        if (this.cardIdForRemove) {
          this.removePublisherCard(this.cardIdForRemove);
        }
      },

      onInfinite() {
        const nextChapter = this.chapters[this.visibleChapterIndex + 1];

        if (isNil(nextChapter)) {
          return this.$refs.infiniteLoading.$emit('$InfiniteLoading:complete');
        }

        return this.fetchChapterCards(nextChapter.id)
          .then(() => {
            this.visibleChapterIndex += 1;
            this.$refs.infiniteLoading.$emit('$InfiniteLoading:loaded');
          });
      },
    },

    beforeMount() {
      this.loading = true;

      const seriesId = this.$route.params.series_id;

      Promise.resolve()
        .then(() => {
          // 1. fetch series if empty
          if (isEmpty(this.series)) {
            return this.fetchSeriesById(seriesId);
          }

          return true;
        })
        .then(() => {
          // 2. check current series exist
          this.seriesItem = this.series[seriesId];

          if (isNil(this.seriesItem)) {
            return Promise.reject('Series not found');
          }

          return true;
        })
        .then(() => {
          // 3. fetch chapters if empty
          if (isEmpty(this.chapters)) {
            return this.fetchSeriesChapters(seriesId);
          }

          return true;
        })
        .then(() => (
          // 4. check visible chapter index
          this.chapters.forEach((chapter, i) => {
            if (!isNil(this.cardsByChapterId[chapter.id])) {
              this.visibleChapterIndex = i;
            }
          })
        ))
        .then(() => {
          this.loading = false;
        });
    },
  };
</script>

<style lang="scss">
  .series-cards {
    .series-cards__header-title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1; /* number of lines to show */
      max-height: 1.5em;    /* fallback */
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/publishers/views/SeriesCards.vue